import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { UsersService } from 'src/app/services/users.service';
import { User } from 'src/app/models/user.model';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'csn-pricing',
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PricingComponent implements OnInit, OnDestroy {
  authenticated = false;
  user?: User;
  isLoading = false;
  checkoutLoading: 'pro' | 'club' | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthenticationService,
    private usersService: UsersService,
    private toast: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.authenticated = this.authService.isAuthenticated();
    if (this.authenticated) {
      this.isLoading = true;
      this.usersService.getCurrent().pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (user: User) => {
          this.user = user;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
    }
  }

  get currentTier(): 'free' | 'pro' | 'club' | null {
    if (!this.user) { return null; }
    const status = this.user.subscriptionStatus;
    if (status === 'active' || status === 'trialing') { return 'club'; }
    // Pro is a per-course one-time purchase tracked via user.tier (server field from COU-252)
    if (this.user.tier === 'pro') { return 'pro'; }
    return 'free';
  }

  async startProCheckout() {
    if (this.checkoutLoading) { return; }
    this.checkoutLoading = 'pro';
    this.cdr.markForCheck();
    try {
      const url = await this.usersService.getCheckoutRedirectUrl('pro');
      window.location.href = url;
    } catch {
      this.checkoutLoading = null;
      this.toast.show('Could not start checkout. Please try again.', { classname: 'bg-danger text-light' });
      this.cdr.markForCheck();
    }
  }

  async startClubCheckout() {
    if (this.checkoutLoading) { return; }
    this.checkoutLoading = 'club';
    this.cdr.markForCheck();
    try {
      const url = await this.usersService.getCheckoutRedirectUrl('club');
      window.location.href = url;
    } catch {
      this.checkoutLoading = null;
      this.toast.show('Could not start checkout. Please try again.', { classname: 'bg-danger text-light' });
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
